import { useContext, useMemo } from 'react';

import type Big from 'big.js';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import { tokens } from '@cast/design-system';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { useWorkloadTrafficDestinationsHistoryQuery } from '@cast/split-me/hooks';
import {
  WorkloadTrafficDestinationHistory,
  WorkloadTrafficDestinationHistoryItem,
} from '@cast/types';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from '@cast/split-me/types';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

dayjs.extend(utc);
dayjs.extend(timezone);

const CROSS_AZ_DESTINATION_COLORS = [
  tokens.theme.data.grey900,
  tokens.theme.data.blue400,
  tokens.theme.data.green500,
  tokens.theme.data.red400,
  tokens.theme.data.yellow500,
  tokens.theme.data.aqua500,
];

const TOP_ITEMS_LIMIT = 5;

type DestinationHistoryItem = WorkloadTrafficDestinationHistoryItem<Big>;
type ValueKey = 'interZoneCost' | 'interZoneBytes';

const getDestinationId = (destination: {
  namespace?: string;
  workloadName?: string;
}) => `${destination.namespace ?? ''}/${destination.workloadName ?? ''}`;

const getDestinationName = (destination: { workloadName?: string }) =>
  destination.workloadName ?? 'unknown';

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (
    typeof value === 'object' &&
    'toNumber' in value &&
    typeof value.toNumber === 'function'
  ) {
    return value.toNumber();
  }

  const parsed = Number(value);

  return Number.isNaN(parsed) ? 0 : parsed;
};

const getItemValue = (
  item: WorkloadTrafficDestinationHistory<Big>,
  valueKey: ValueKey
): number => {
  return toNumber(item.egress?.[valueKey]) + toNumber(item.ingress?.[valueKey]);
};

const getDestinationTotalValue = (
  destination: DestinationHistoryItem,
  valueKey: ValueKey
): number => {
  return (destination.history ?? []).reduce(
    (acc, item) => acc + getItemValue(item, valueKey),
    0
  );
};

const buildTopItems = (
  data: DestinationHistoryItem[],
  valueKey: ValueKey
): TopMostExpensiveGroup[] => {
  const items = data
    .map((destination) => ({
      id: getDestinationId(destination.destination ?? {}),
      name: getDestinationName(destination.destination ?? {}),
      cost: getDestinationTotalValue(destination, valueKey),
    }))
    .filter((item) => item.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, TOP_ITEMS_LIMIT);

  return items.map((item, index) => ({
    ...item,
    color:
      CROSS_AZ_DESTINATION_COLORS[index % CROSS_AZ_DESTINATION_COLORS.length],
  }));
};

const buildDatapoints = (
  data: DestinationHistoryItem[],
  topItems: TopMostExpensiveGroup[],
  valueKey: ValueKey
): TopMostExpensiveGroupDatapoint => {
  const topItemIds = new Set(topItems.map((item) => item.id));
  const byTimestamp: Record<
    string,
    { timestamp: string; values: Record<string, number | null> }
  > = {};

  data.forEach((destination) => {
    const id = getDestinationId(destination.destination ?? {});

    if (!topItemIds.has(id)) {
      return;
    }

    destination.history?.forEach((item) => {
      const timestamp = item.timestamp ?? '';

      if (!timestamp) {
        return;
      }

      byTimestamp[timestamp] ??= {
        timestamp,
        values: {},
      };

      byTimestamp[timestamp].values[id] =
        (byTimestamp[timestamp].values[id] ?? 0) + getItemValue(item, valueKey);
    });
  });

  return Object.values(byTimestamp).sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp)
  );
};

export const useCrossAzCommunicationData = (valueKey: ValueKey) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { getRange, timeZone, key } = useInventoryWorkloadsTimeRangeSelection();

  const range = useMemo(() => getRange(), [key]);
  const from = useMemo(() => dayjs.utc(range.from), [range.from]);
  const to = useMemo(() => dayjs.utc(range.to), [range.to]);

  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadTrafficDestinationsHistoryQuery({
      clusterId: cluster.id,
      namespace: workload?.namespace ?? '',
      workloadType: workload?.kind ?? '',
      workloadName: workload?.name ?? '',
      range: [from, to],
      limit: TOP_ITEMS_LIMIT,
      enabled: !!workload && !!cluster.id,
    });

  const topItems = useMemo(
    () => buildTopItems(data ?? [], valueKey),
    [data, valueKey]
  );

  const datapoints = useMemo(
    () => buildDatapoints(data ?? [], topItems, valueKey),
    [data, topItems, valueKey]
  );

  const hasWorkload = !!workload && !!cluster.id;

  return {
    topItems,
    datapoints,
    isLoading: isLoading || !hasWorkload || (!data && !error),
    isFetching,
    error,
    refetch,
    timeZone,
  };
};
